import React, { useEffect, useState } from 'react';
import PageHeaderText from 'components/base/page-header-text';
import { useNavigate } from 'react-router-dom';
import MetadataDropdown from 'components/metadata/metadata-dropdown';
import { apiService } from 'services/base.service';
import PageText from 'components/base/page-text';
import {
  Breadcrumbs,
  FileUploader,
  Input,
  Multiselect,
  ToastMessage
} from '@cokitchen/cokitchen-components';
import { BrandOption, BrandType } from 'utils/types';
import useDashboardContext from 'contexts/dashboard.context';
import { MultiselectItem } from '@cokitchen/cokitchen-components/dist/form/multiselect/types';
import { toast } from 'react-toastify';
import FeedImagePreviewCard from './feed-image-preview';
import SelectBrand from 'components/select-brand';
import { STORAGE_KEYS } from 'utils/constants';
import SingleBottomNav from 'components/base/single-bottom-nav';
import { MetadataType } from 'pages/create-brand/create-brand-metadata/types';
import { MetadataItemType } from 'pages/metadata/types';

const NewFeed = () => {
  const { activeCokitchen, category } = useDashboardContext();
  const [keywordsList, setKeywordsList] = useState<MetadataType[]>([]);
  const [loading, setLoading] = useState(false);
  const [addFeedLoading, setAddFeedLoading] = useState(false);
  const [existingImage, setExistingImage] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<BrandOption>();
  const [selectedMeal, setSelectedMeal] = useState([] as string[]);
  const [meals, setMeals] = useState([] as MultiselectItem[]);
  const [selectedKeywords, setSelectedKeywords] = useState<MetadataItemType[]>([]);
  const [imageUrl, setImageUrl] = useState('');
  const [showForm, setShowForm] = useState(true);
  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) as string;
  const navigate = useNavigate();
  const fetchMeals = async () => {
    if (!selectedBrand?.value) return;
    try {
      const res = await apiService.get(`internal/brand/meals/${selectedBrand.value}`, {
        isInternal: true
      });
      const meals = res?.data?.meals?.map((meal: BrandType) => ({
        text: meal?.name,
        value: meal?.images?.[0]
      }));
      setMeals(meals);
    } catch {
      setMeals([]);
    }
  };
  const fetchKeywords = async () => {
    try {
      setLoading(true);
      const res = await apiService.post('marketing/meal-keywords', {
        data: {
          item_type: category?.toUpperCase(),
          exist: true
        }
      });
      const { data } = res.data;

      setKeywordsList(data);
    } catch (e: any) {
      toast(<ToastMessage type='error' message='An Error Occured' />);
    } finally {
      setLoading(false);
    }
  };

  const addFeed = async () => {
    const image = !existingImage ? [imageUrl] : selectedMeal;
    try {
      setAddFeedLoading(true);
      const payload: {
        cokitchen_id: string;
        meal_keyword_id: string;
        item_type: string;
        images?: string[] | string;
      } = {
        cokitchen_id: activeCokitchen.id,
        meal_keyword_id: selectedKeywords?.[0]?.id,
        item_type: category.toUpperCase()
      };
      if (image?.[0] !== '') {
        payload.images = image;
      }

      const res = await apiService.post('marketing/meal-explore-keyword', { data: payload });
      if (res?.data) {
        toast(<ToastMessage type='success' message='Feed Created Successfully' />);
        navigate(`/feeds`);
      }
    } catch (e: any) {
      toast(<ToastMessage type='error' message={e?.response?.data?.message} />);
    } finally {
      setAddFeedLoading(false);
    }
  };

  const onKeywordTagClick = (item: MetadataItemType) => {
    const exists = !!selectedKeywords.find((k) => k.id === item.id);
    if (!exists) {
      setSelectedKeywords([item]);
    } else {
      setSelectedKeywords((words) => words.filter((w) => w.id !== item.id));
    }
  };

  const createMetadata = async (name: string) => {
    try {
      const res = await apiService.post('marketing/create-meal-keyword', {
        data: {
          name,
          item_type: category?.toUpperCase()
        }
      });
      const { message, data } = res.data;
      toast(<ToastMessage type='success' message={message} />);
      setKeywordsList((p) => [{ name, id: data?.id, images: undefined }, ...p]);
    } catch {
      toast(<ToastMessage type='error' message='There was a problem adding metadata' />);
    }
  };

  const breadCrumbs = [
    { name: 'Feeds', path: `/feeds` },
    {
      name: 'Create Feed',
      path: ''
    }
  ];

  useEffect(() => {
    fetchKeywords();
  }, []);

  const resetForm = () => {
    setImageUrl('');
    setSelectedMeal([]);
    if (selectedKeywords.length !== 0) {
      if (selectedKeywords?.[0]?.images === null || selectedKeywords?.[0]?.images === undefined) {
        setShowForm(true);
      } else {
        setShowForm(false);
      }
    } else {
      setShowForm(true);
    }
  };

  useEffect(() => {
    resetForm();
  }, [selectedKeywords]);

  useEffect(() => {
    fetchMeals();
  }, [selectedBrand]);

  return (
    <div className='max-w-[755px]'>
      <Breadcrumbs breadcrumbs={breadCrumbs} />
      <PageHeaderText title='Add a new item' />

      {loading ? (
        <PageText text='Loading...' />
      ) : (
        <div className='space-y-6'>
          <MetadataDropdown
            title='keyword'
            description='Select keyword'
            metadataList={keywordsList}
            selectedMetadata={selectedKeywords}
            onCreate={createMetadata}
            onTagClick={onKeywordTagClick}
          />

          {showForm && (
            <div className='space-y-6'>
              <FileUploader
                token={token}
                setFileUrls={(urls) => {
                  setImageUrl(urls[0]);
                }}
                fileUrls={imageUrl ? [imageUrl] : []}
              />

              <div className='w-full py-4 px-6 border border-[#EEECEC] bg-[#F9F6F6] rounded-md'>
                <Input
                  type='checkbox'
                  label='Use image from an already existing meal '
                  checked={existingImage}
                  onChange={() => setExistingImage(!existingImage)}
                  className='text-sm'
                  name='existingImage'
                />
              </div>

              {existingImage && (
                <div>
                  <SelectBrand
                    brands={selectedBrand ? [selectedBrand] : []}
                    setBrands={(b) => setSelectedBrand(b[0])}
                  />

                  {meals.length > 0 && (
                    <Multiselect
                      placeholder='Select Meal'
                      values={selectedMeal}
                      options={meals}
                      onChange={setSelectedMeal}
                      isSingle
                    />
                  )}
                </div>
              )}
              {selectedMeal.length > 0 && <FeedImagePreviewCard data={selectedMeal[0]} />}
            </div>
          )}

          {selectedKeywords[0]?.images && (
            <FeedImagePreviewCard data={selectedKeywords?.[0]?.images?.[0]} />
          )}
          <SingleBottomNav loading={addFeedLoading} onConfirm={addFeed} className='mt-12' />
        </div>
      )}
    </div>
  );
};

export default NewFeed;
